import { useEffect, useRef } from 'react';

const THEMES = {
  embers: { label: 'Embers', icon: '🔥' },
  ice_rain: { label: 'Ice Rain', icon: '🧊' },
  music_notes: { label: 'Music Notes', icon: '🎵' },
  rain: { label: 'Rain', icon: '🌧' },
  snow: { label: 'Snow', icon: '❄' },
  electric: { label: 'Electric', icon: '⚡' },
  stars: { label: 'Stars', icon: '🌌' },
  audio_waveform: { label: 'Audio Waveform', icon: '🎚' },
  cinematic_smoke: { label: 'Cinematic Smoke', icon: '💨' },
  digital_rain: { label: 'Digital Rain', icon: '🟢' },
  galaxy_dust: { label: 'Galaxy Dust', icon: '🌠' },
  video: { label: 'Video', icon: '🎬' },
  off: { label: 'Off', icon: '⬛' },
};

export { THEMES };

export function MotionTheme({ theme = 'embers', videoUrl = '', className = '', settings = {} }) {
  const canvasRef = useRef(null);
  const speedMult = (settings.speed || 50) / 50;
  const densityMult = (settings.density || 50) / 50;
  const opacityMult = (settings.opacity || 80) / 100;

  useEffect(() => {
    if (theme === 'video' || theme === 'off') return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const W = () => canvas.width;
    const H = () => canvas.height;

    // Detect mobile for reduced particle counts
    const mobile = window.innerWidth < 768;
    const MAX = Math.floor((mobile ? 25 : 40) * densityMult);
    const SPAWN_RATE = Math.max(2, Math.floor((mobile ? 6 : 4) / densityMult));

    // Parse custom colors
    const hexToRgb = (hex) => {
      const r = parseInt(hex.slice(1,3), 16) || 0;
      const g = parseInt(hex.slice(3,5), 16) || 0;
      const b = parseInt(hex.slice(5,7), 16) || 0;
      return [r, g, b];
    };
    const pColor = settings.primary_color ? hexToRgb(settings.primary_color) : null;
    const sColor = settings.secondary_color ? hexToRgb(settings.secondary_color) : null;

    // ── Theme-specific particle factories ──

    const spawnEmber = () => ({
      x: Math.random() * W(), y: H() + 5,
      size: Math.random() * 2.5 + 0.8,
      vx: (Math.random() - 0.5) * 0.8 * speedMult, vy: -(Math.random() * 1.5 + 0.5) * speedMult,
      life: 1, decay: Math.random() * 0.008 + 0.004,
      wobble: Math.random() * Math.PI * 2, ws: Math.random() * 0.03 + 0.01,
      c: Math.random() < 0.55 ? 0 : Math.random() < 0.5 ? 1 : 2,
    });

    const spawnIce = () => ({
      x: Math.random() * W(), y: -10,
      size: Math.random() * 4 + 2,
      vx: (Math.random() - 0.5) * 0.5, vy: Math.random() * 2 + 1.5,
      life: 1, decay: Math.random() * 0.003 + 0.002,
      rot: Math.random() * Math.PI * 2, rs: (Math.random() - 0.5) * 0.04,
      facets: Math.floor(Math.random() * 3) + 5,
    });

    const NOTE_CHARS = ['\u266A', '\u266B', '\u2669', '\u266C'];
    const spawnNote = () => ({
      x: Math.random() * W(), y: -20,
      size: Math.random() * 16 + 12,
      vx: (Math.random() - 0.5) * 0.6, vy: Math.random() * 1.5 + 0.8,
      life: 1, decay: Math.random() * 0.004 + 0.002,
      wobble: Math.random() * Math.PI * 2, ws: Math.random() * 0.03 + 0.01,
      rot: (Math.random() - 0.5) * 0.5,
      char: NOTE_CHARS[Math.floor(Math.random() * NOTE_CHARS.length)],
      c: Math.random() < 0.5 ? 0 : Math.random() < 0.5 ? 1 : 2,
    });

    const spawnRain = () => ({
      x: Math.random() * W(), y: -10,
      len: Math.random() * 15 + 8,
      vx: -0.5 * speedMult, vy: (Math.random() * 6 + 8) * speedMult,
      life: 1, decay: 0,
      alpha: Math.random() * 0.3 + 0.15,
    });

    const spawnSnow = () => ({
      x: Math.random() * W(), y: -5,
      size: Math.random() * 3 + 1,
      vx: (Math.random() - 0.5) * 0.4 * speedMult, vy: (Math.random() * 1 + 0.3) * speedMult,
      life: 1, decay: Math.random() * 0.002 + 0.001,
      wobble: Math.random() * Math.PI * 2, ws: Math.random() * 0.02 + 0.005,
    });

    const spawnElectric = () => {
      const startX = Math.random() * W();
      const startY = Math.random() * H() * 0.3;
      const segments = [];
      let sx = startX, sy = startY;
      const count = Math.floor(Math.random() * 4) + 3;
      for (let i = 0; i < count; i++) {
        const ex = sx + (Math.random() - 0.5) * 60;
        const ey = sy + Math.random() * 40 + 15;
        segments.push({ x1: sx, y1: sy, x2: ex, y2: ey });
        sx = ex; sy = ey;
      }
      return { segments, life: 1, decay: 0.04 + Math.random() * 0.03 };
    };

    const spawnStar = () => ({
      x: Math.random() * W(), y: Math.random() * H(),
      size: Math.random() * 1.5 + 0.5,
      twinkle: Math.random() * Math.PI * 2,
      ts: Math.random() * 0.03 + 0.01,
      brightness: Math.random() * 0.5 + 0.3,
      c: Math.random() < 0.7 ? 0 : Math.random() < 0.5 ? 1 : 2,
      drift: (Math.random() - 0.5) * 0.1,
    });

    // ── New ambient presets ──

    // Audio Waveform: simulated audio frequency band — drawn as one persistent "particle"
    const spawnWaveform = () => ({
      phase: Math.random() * Math.PI * 2,
      life: 1, decay: 0, // doesn't die
    });

    // Cinematic Smoke: volumetric smoke puff with embers
    const spawnSmoke = () => ({
      x: Math.random() * W(), y: H() + 20,
      size: Math.random() * 60 + 40,
      vx: (Math.random() - 0.5) * 0.3 * speedMult,
      vy: -(Math.random() * 0.6 + 0.3) * speedMult,
      life: 1, decay: Math.random() * 0.004 + 0.002,
      wobble: Math.random() * Math.PI * 2,
      ws: Math.random() * 0.01 + 0.005,
      hue: Math.random() * 20 - 10,
      isEmber: Math.random() < 0.15,
    });

    // Digital Rain: Matrix-style vertical light streaks with intermittent pulse
    const spawnDigiRain = () => ({
      x: Math.floor(Math.random() * (W() / 14)) * 14,
      y: -Math.random() * H(),
      speed: Math.random() * 3 + 2.5,
      tail: Math.floor(Math.random() * 15 + 10),
      brightness: Math.random() * 0.5 + 0.5,
      pulsePhase: Math.random() * Math.PI * 2,
    });

    // Galaxy Dust: slow drifting nebula motes with parallax
    const spawnGalaxy = () => {
      const depth = Math.random();
      return {
        x: Math.random() * W(), y: Math.random() * H(),
        size: depth * 2 + 0.3,
        vx: (Math.random() - 0.5) * 0.15 * depth,
        vy: (Math.random() - 0.5) * 0.08 * depth,
        twinkle: Math.random() * Math.PI * 2,
        ts: Math.random() * 0.015 + 0.005,
        brightness: depth * 0.7 + 0.2,
        c: Math.random() < 0.5 ? 0 : Math.random() < 0.5 ? 1 : 2,
        depth,
      };
    };

    // Spawner map
    const spawners = {
      embers: spawnEmber, ice_rain: spawnIce, music_notes: spawnNote,
      rain: spawnRain, snow: spawnSnow, electric: spawnElectric, stars: spawnStar,
      audio_waveform: spawnWaveform,
      cinematic_smoke: spawnSmoke,
      digital_rain: spawnDigiRain,
      galaxy_dust: spawnGalaxy,
    };

    const EMBER_COLORS = pColor ? [pColor, sColor || pColor, [180, 20, 20]] : [[255, 120, 20], [218, 165, 32], [180, 20, 20]];
    const ICE_COLORS = pColor ? [pColor, sColor || pColor, [100, 180, 220]] : [[150, 220, 255], [200, 240, 255], [100, 180, 220]];
    const NOTE_COLORS = pColor ? [pColor, sColor || pColor, [255, 255, 255]] : [[255, 59, 48], [218, 165, 32], [255, 255, 255]];
    const STAR_COLORS = pColor ? [pColor, sColor || pColor, [255, 220, 180]] : [[255, 255, 255], [200, 220, 255], [255, 220, 180]];
    const GALAXY_COLORS = pColor ? [pColor, sColor || pColor, [200, 180, 255]] : [[255, 200, 220], [180, 200, 255], [200, 255, 240]];
    const DIGIRAIN_COLOR = pColor || [0, 255, 140];
    const SMOKE_BASE = pColor || [180, 60, 30];
    const SMOKE_EMBER = sColor || [255, 150, 40];

    // Pre-populate for stars (they don't move much)
    if (theme === 'stars') {
      for (let i = 0; i < MAX; i++) particles.push(spawnStar());
    } else if (theme === 'galaxy_dust') {
      for (let i = 0; i < MAX * 2; i++) particles.push(spawnGalaxy());
    } else if (theme === 'audio_waveform') {
      // single "waveform particle" drives the whole render
      particles.push(spawnWaveform());
    } else if (theme === 'digital_rain') {
      for (let i = 0; i < Math.floor(MAX * 0.8); i++) {
        const p = spawnDigiRain();
        p.y = Math.random() * H();
        particles.push(p);
      }
    } else if (theme === 'cinematic_smoke') {
      for (let i = 0; i < Math.floor(MAX * 0.4); i++) {
        const p = spawnSmoke();
        p.y = Math.random() * H();
        p.life = Math.random() * 0.6 + 0.3;
        particles.push(p);
      }
    } else if (theme === 'embers' || theme === 'ice_rain' || theme === 'music_notes' || theme === 'snow') {
      // Pre-populate so particles are visible immediately
      const spawn = spawners[theme];
      for (let i = 0; i < Math.floor(MAX * 0.6); i++) {
        const p = spawn();
        p.y = Math.random() * H();
        p.life = Math.random() * 0.5 + 0.3;
        // For rising themes (embers), spread velocity so some are mid-flight
        if (theme === 'embers') {
          p.vy = -(Math.random() * 0.8 + 0.3);
        }
        particles.push(p);
      }
    }

    let frame = 0;
    const animate = () => {
      frame++;
      // Throttle to ~20fps for better performance
      if (frame % 3 !== 0) { animId = requestAnimationFrame(animate); return; }

      ctx.clearRect(0, 0, W(), H());
      const spawn = spawners[theme];
      if (!spawn) { animId = requestAnimationFrame(animate); return; }

      // Spawn
      if (theme === 'rain') {
        if (frame % 2 === 0 && particles.length < (mobile ? 40 : 80)) particles.push(spawnRain());
      } else if (theme === 'electric') {
        if (frame % 20 === 0 && particles.length < 5) particles.push(spawnElectric());
      } else if (theme === 'cinematic_smoke') {
        if (frame % SPAWN_RATE === 0 && particles.length < MAX) particles.push(spawnSmoke());
      } else if (theme === 'digital_rain') {
        if (frame % 2 === 0 && particles.length < MAX * 1.5) particles.push(spawnDigiRain());
      } else if (theme === 'audio_waveform') {
        // no spawn needed — one persistent driver particle
      } else if (theme === 'galaxy_dust') {
        // pre-populated, no re-spawn; keep count steady if particles drift off
        if (particles.length < MAX * 2) particles.push(spawnGalaxy());
      } else if (theme !== 'stars') {
        if (frame % SPAWN_RATE === 0 && particles.length < MAX) particles.push(spawn());
      }

      // Update & draw
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];

        if (theme === 'embers') {
          p.wobble += p.ws;
          p.x += p.vx + Math.sin(p.wobble) * 0.3;
          p.y += p.vy; p.life -= p.decay;
          if (p.life <= 0 || p.y < -10) { particles.splice(i, 1); continue; }
          const [r, g, b] = EMBER_COLORS[p.c];
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},${p.life * 0.8 * opacityMult})`;
          ctx.fill();

        } else if (theme === 'ice_rain') {
          p.x += p.vx; p.y += p.vy; p.rot += p.rs; p.life -= p.decay;
          if (p.life <= 0 || p.y > H() + 20) { particles.splice(i, 1); continue; }
          const [r, g, b] = ICE_COLORS[p.facets % 3];
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.beginPath();
          for (let j = 0; j < p.facets; j++) {
            const angle = (j / p.facets) * Math.PI * 2 - Math.PI / 2;
            const r2 = p.size * (j % 2 === 0 ? 1 : 0.5);
            if (j === 0) ctx.moveTo(Math.cos(angle) * r2, Math.sin(angle) * r2);
            else ctx.lineTo(Math.cos(angle) * r2, Math.sin(angle) * r2);
          }
          ctx.closePath();
          ctx.fillStyle = `rgba(${r},${g},${b},${p.life * 0.7 * opacityMult})`;
          ctx.fill();
          ctx.strokeStyle = `rgba(255,255,255,${p.life * 0.3 * opacityMult})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
          ctx.restore();

        } else if (theme === 'music_notes') {
          p.wobble += p.ws;
          p.x += p.vx + Math.sin(p.wobble) * 0.5;
          p.y += p.vy; p.life -= p.decay;
          if (p.life <= 0 || p.y > H() + 30) { particles.splice(i, 1); continue; }
          const [r, g, b] = NOTE_COLORS[p.c];
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.font = `${p.size * p.life}px serif`;
          ctx.fillStyle = `rgba(${r},${g},${b},${p.life * 0.75 * opacityMult})`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(p.char, 0, 0);
          ctx.restore();

        } else if (theme === 'rain') {
          p.x += p.vx; p.y += p.vy;
          if (p.y > H() + 20) { particles.splice(i, 1); continue; }
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + p.vx * 2, p.y - p.len);
          ctx.strokeStyle = `rgba(180, 210, 240, ${p.alpha * opacityMult})`;
          ctx.lineWidth = 1;
          ctx.stroke();

        } else if (theme === 'snow') {
          p.wobble += p.ws;
          p.x += p.vx + Math.sin(p.wobble) * 0.3;
          p.y += p.vy; p.life -= p.decay;
          if (p.life <= 0 || p.y > H() + 10) { particles.splice(i, 1); continue; }
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${p.life * 0.6 * opacityMult})`;
          ctx.fill();

        } else if (theme === 'electric') {
          p.life -= p.decay;
          if (p.life <= 0) { particles.splice(i, 1); continue; }
          ctx.beginPath();
          for (const seg of p.segments) {
            ctx.moveTo(seg.x1, seg.y1);
            ctx.lineTo(seg.x2, seg.y2);
          }
          ctx.strokeStyle = `rgba(140, 180, 255, ${p.life * opacityMult})`;
          ctx.lineWidth = 2;
          ctx.stroke();
          // Glow line
          ctx.strokeStyle = `rgba(200, 220, 255, ${p.life * 0.3 * opacityMult})`;
          ctx.lineWidth = 5;
          ctx.stroke();

        } else if (theme === 'stars') {
          p.twinkle += p.ts;
          p.x += p.drift;
          if (p.x < 0) p.x = W();
          if (p.x > W()) p.x = 0;
          const alpha = p.brightness * (0.5 + 0.5 * Math.sin(p.twinkle)) * opacityMult;
          const [r, g, b] = STAR_COLORS[p.c];
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
          ctx.fill();

        } else if (theme === 'audio_waveform') {
          // Draw a flowing multi-layer waveform — single particle drives the whole effect
          p.phase += 0.025 * speedMult;
          const layers = 3;
          const midY = H() / 2;
          for (let layer = 0; layer < layers; layer++) {
            ctx.beginPath();
            const amp = (H() / 6) * (1 - layer * 0.25);
            const freq = 0.008 + layer * 0.004;
            const speed = p.phase * (1 + layer * 0.3);
            for (let x = 0; x <= W(); x += 3) {
              const y = midY
                + Math.sin(x * freq + speed) * amp
                + Math.sin(x * freq * 2.3 + speed * 1.7) * amp * 0.35
                + Math.cos(x * freq * 0.5 + speed * 0.6) * amp * 0.25;
              if (x === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            const [pr, pg, pb] = pColor || [255, 59, 48];
            const [sr, sg, sb] = sColor || [218, 165, 32];
            const grad = ctx.createLinearGradient(0, 0, W(), 0);
            grad.addColorStop(0, `rgba(${pr},${pg},${pb},${(0.5 - layer * 0.12) * opacityMult})`);
            grad.addColorStop(0.5, `rgba(${sr},${sg},${sb},${(0.6 - layer * 0.12) * opacityMult})`);
            grad.addColorStop(1, `rgba(${pr},${pg},${pb},${(0.5 - layer * 0.12) * opacityMult})`);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 2 - layer * 0.5;
            ctx.stroke();
          }

        } else if (theme === 'cinematic_smoke') {
          p.wobble += p.ws;
          p.x += p.vx + Math.sin(p.wobble) * 0.4;
          p.y += p.vy;
          p.life -= p.decay;
          if (p.life <= 0 || p.y < -80) { particles.splice(i, 1); continue; }
          if (p.isEmber) {
            const [r, g, b] = SMOKE_EMBER;
            const size = p.size * 0.08 * p.life;
            const glowGrad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 4);
            glowGrad.addColorStop(0, `rgba(${r},${g},${b},${p.life * 0.9 * opacityMult})`);
            glowGrad.addColorStop(1, `rgba(${r},${g},${b},0)`);
            ctx.fillStyle = glowGrad;
            ctx.beginPath();
            ctx.arc(p.x, p.y, size * 4, 0, Math.PI * 2);
            ctx.fill();
          } else {
            const [r, g, b] = SMOKE_BASE;
            const rad = p.size * (1.5 - p.life * 0.5);
            const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
            grad.addColorStop(0, `rgba(${r},${g},${b},${p.life * 0.22 * opacityMult})`);
            grad.addColorStop(0.5, `rgba(${r},${g},${b},${p.life * 0.10 * opacityMult})`);
            grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
            ctx.fill();
          }

        } else if (theme === 'digital_rain') {
          p.y += p.speed * speedMult;
          p.pulsePhase += 0.1;
          if (p.y - p.tail * 14 > H()) { particles.splice(i, 1); continue; }
          const [r, g, b] = DIGIRAIN_COLOR;
          // Head glow
          const headAlpha = (0.9 + 0.1 * Math.sin(p.pulsePhase)) * p.brightness * opacityMult;
          ctx.fillStyle = `rgba(${r},${g},${b},${headAlpha})`;
          ctx.fillRect(p.x, p.y, 2, 12);
          // White-ish head
          ctx.fillStyle = `rgba(220,255,230,${headAlpha})`;
          ctx.fillRect(p.x, p.y, 2, 3);
          // Trailing tail
          for (let t = 1; t < p.tail; t++) {
            const trailY = p.y - t * 14;
            if (trailY < 0) break;
            const tAlpha = (1 - t / p.tail) * 0.5 * p.brightness * opacityMult;
            ctx.fillStyle = `rgba(${r},${g},${b},${tAlpha})`;
            ctx.fillRect(p.x, trailY, 2, 10);
          }

        } else if (theme === 'galaxy_dust') {
          p.twinkle += p.ts;
          p.x += p.vx;
          p.y += p.vy;
          // wrap around
          if (p.x < -10) p.x = W() + 10;
          if (p.x > W() + 10) p.x = -10;
          if (p.y < -10) p.y = H() + 10;
          if (p.y > H() + 10) p.y = -10;
          const alpha = p.brightness * (0.6 + 0.4 * Math.sin(p.twinkle)) * opacityMult;
          const [r, g, b] = GALAXY_COLORS[p.c];
          // Soft halo for larger particles
          if (p.size > 1) {
            const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4);
            halo.addColorStop(0, `rgba(${r},${g},${b},${alpha * 0.5})`);
            halo.addColorStop(1, `rgba(${r},${g},${b},0)`);
            ctx.fillStyle = halo;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 4, 0, Math.PI * 2);
            ctx.fill();
          }
          // Core
          ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animId = requestAnimationFrame(animate);
    };

    animate();
    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, [theme, settings.speed, settings.density, settings.opacity, settings.primary_color, settings.secondary_color]);

  if (theme === 'off') return null;

  if (theme === 'video') {
    if (!videoUrl) return null;
    return (
      <div className={`absolute inset-0 w-full h-full overflow-hidden pointer-events-none ${className}`} data-testid="motion-theme-video">
        <video
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: opacityMult * 0.3 }}
          src={videoUrl}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-transparent to-[#0A0A0A]/80" />
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid={`motion-theme-${theme}`}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
}
